import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, PackageOpen, AlertCircle } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function Cart() {
  const { cart, updateCartItem, removeFromCart, removeCustomBox, loading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleUpdateQuantity = async (item, newQuantity) => {
    try {
      await updateCartItem(item.product_id, newQuantity, item.subscription_type);
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleRemoveBox = async (boxId) => {
    try {
      await removeCustomBox(boxId);
      toast.success('Custom box removed');
    } catch (error) {
      toast.error('Failed to remove box');
    }
  };

  const customBoxes = cart.custom_boxes || [];
  const minOrder = cart.min_order || 40;
  const belowMinimum = cart.subtotal > 0 && cart.subtotal < minOrder;
  const deliveryFee = cart.subtotal >= 50 ? 0 : 4.99;
  const discount = cart.discount || 0;
  const total = cart.subtotal - discount + deliveryFee;

  const getSubscriptionLabel = (type) => {
    const labels = {
      'one-time': 'One-Time',
      'weekly': 'Weekly',
      'biweekly': 'Bi-Weekly',
      'monthly': 'Monthly'
    };
    return labels[type] || type;
  };

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="cart-page">
      <div className="container-custom py-12">
        <h1 className="font-playfair text-4xl font-bold text-dark mb-8" data-testid="cart-title">Your Cart</h1>

        {!user ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-forest/10">
            <ShoppingBag className="w-16 h-16 text-dark/20 mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-bold text-dark mb-4">Sign in to view your cart</h2>
            <p className="font-manrope text-dark/60 mb-8">Please sign in to add items to your cart and checkout.</p>
            <Link to="/login" className="btn-primary" data-testid="cart-login-btn">
              Sign In
            </Link>
          </div>
        ) : cart.items.length === 0 && customBoxes.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-forest/10">
            <ShoppingBag className="w-16 h-16 text-dark/20 mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-bold text-dark mb-4">Your cart is empty</h2>
            <p className="font-manrope text-dark/60 mb-8">Start shopping to fill your cart with fresh, local goodness!</p>
            <Link to="/shop" className="btn-primary" data-testid="cart-shop-btn">
              Browse Boxes
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cart.items.map((item) => (
                <div
                  key={`${item.product_id}-${item.subscription_type}`}
                  className="bg-white rounded-2xl p-6 border border-forest/10 flex flex-col sm:flex-row gap-6"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full sm:w-32 h-32 object-cover rounded-xl"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-grow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <Link to={`/product/${item.product_id}`} className="font-playfair text-xl font-semibold text-dark hover:text-forest transition-colors">
                          {item.product.name}
                        </Link>
                        <p className="text-sm text-dark/60 font-manrope">{item.product.farm_name}</p>
                      </div>
                      <button
                        onClick={() => handleRemove(item.product_id)}
                        className="p-2 text-dark/40 hover:text-red-500 transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <span className="inline-block badge-local text-xs mb-4">
                      {getSubscriptionLabel(item.subscription_type)}
                    </span>

                    <div className="flex justify-between items-center">
                      {/* Quantity */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-8 h-8 rounded-full border border-forest/20 flex items-center justify-center hover:border-forest transition-colors disabled:opacity-50"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-manrope font-bold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                          className="w-8 h-8 rounded-full border border-forest/20 flex items-center justify-center hover:border-forest transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Price */}
                      <p className="font-playfair text-xl font-bold text-dark">
                        ${item.item_total.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {customBoxes.map((box) => (
                <div key={box.box_id} className="bg-white rounded-2xl p-6 border border-forest/10" data-testid={`cart-custom-box-${box.box_id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <PackageOpen className="w-6 h-6 text-forest" />
                      </div>
                      <div>
                        <p className="font-playfair text-xl font-semibold text-dark">Build Your Own {box.tier_name}</p>
                        <p className="text-sm text-dark/60 font-manrope">{box.item_count} items · {box.total_weight_lb} lb</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveBox(box.box_id)}
                      className="p-2 text-dark/40 hover:text-red-500 transition-colors"
                      data-testid={`remove-custom-box-${box.box_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {box.items.map((it) => (
                      <span key={it.item_id} className="badge-local text-xs">{it.name} x{it.quantity}</span>
                    ))}
                  </div>
                  {box.over_capacity && (
                    <p className="text-xs text-gold-dark font-manrope mb-2">Over the usual weight guideline for this size &mdash; that's okay if you're fine with a fuller box.</p>
                  )}
                  <p className="font-playfair text-xl font-bold text-dark text-right">${box.box_total.toFixed(2)}</p>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-8 border border-forest/10 sticky top-28" data-testid="order-summary">
                <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between font-manrope">
                    <span className="text-dark/60">Subtotal</span>
                    <span className="font-medium text-dark">${cart.subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between font-manrope" data-testid="cart-discount-row">
                      <span className="text-forest">{cart.discount_label || 'Discount'}</span>
                      <span className="font-medium text-forest">-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  {cart.free_item && (
                    <div className="flex justify-between font-manrope text-sm" data-testid="cart-free-item-row">
                      <span className="text-forest">+ 1 free {cart.free_item.name}</span>
                      <span className="text-forest">$0.00</span>
                    </div>
                  )}
                  <div className="flex justify-between font-manrope">
                    <span className="text-dark/60">Delivery</span>
                    <span className="font-medium text-dark">
                      {deliveryFee === 0 ? 'Free' : `$${deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  {deliveryFee > 0 && (
                    <p className="text-sm text-forest font-manrope">
                      Add ${(50 - cart.subtotal).toFixed(2)} more for free delivery
                    </p>
                  )}
                </div>

                <div className="border-t border-forest/10 pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="font-playfair text-xl font-bold text-dark">Total</span>
                    <span className="font-playfair text-xl font-bold text-dark" data-testid="cart-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                {belowMinimum && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mb-4" data-testid="min-order-warning">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs font-manrope text-red-600">
                      A minimum order of ${minOrder.toFixed(0)} is required. Add ${(minOrder - cart.subtotal).toFixed(2)} more to check out.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => navigate('/checkout')}
                  disabled={belowMinimum}
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>

                <p className="text-center text-sm text-dark/50 font-manrope mt-4">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
